import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  message,
  DatePicker,
  Space,
  Typography,
  Row,
  Col,
  Form,
  Empty,
  Select,
  Input,
  Table,
  Modal,
  Descriptions,
} from "antd";
import {
  CalendarOutlined,
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined,
  UserOutlined,
  GlobalOutlined,
  EyeOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import logActionAPI from "../../../api/logAction/logAction";
import { formatDateTimeCustomize } from "../../../utils/dateTime";
import { getDisplayNameFromUser } from "../../../utils/userDisplayName";
import ImportExportMenu from "../../../components/cms/ImportExportMenu";
import CmsTableCard from "../../../components/cms/CmsTableCard";
import { border, radius, semantic, shadow, spacing } from "../../../theme/tokens";
import StatusTag from "../../../components/cms/StatusTag";

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// Tên model ngắn từ loggable_type (Modules\X\Models\Branch -> Branch)
const modelShortName = (type) => (type ? String(type).split("\\").pop() : null);

// Hiển thị 1 giá trị trong bảng diff (xử lý null/bool/object)
const renderValue = (val) => {
  if (val === null || val === undefined || val === "") {
    return <StatusTag>— trống —</StatusTag>;
  }
  if (typeof val === "boolean") {
    return <StatusTag>{val ? "Có" : "Không"}</StatusTag>;
  }
  if (typeof val === "object") {
    return <code style={{ fontSize: 12 }}>{JSON.stringify(val)}</code>;
  }
  return <span>{String(val)}</span>;
};

const LogActionScreen = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [logData, setLogData] = useState([]);
  const [actions, setActions] = useState({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 30, total: 0 });
  const [detail, setDetail] = useState(null); // bản ghi đang xem chi tiết
  const [appliedParams, setAppliedParams] = useState({}); // bộ lọc đang áp dụng (cho export)

  const notifyError = (error, fallback) => {
    const status = error?.response?.status;
    const msg = error?.response?.data?.message;
    if (status === 401) return message.error("Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!");
    if (status === 403) return message.error("Bạn không có quyền truy cập chức năng này!");
    if (error?.request && !error?.response) {
      return message.error("Không thể kết nối tới server. Vui lòng kiểm tra kết nối mạng!");
    }
    return message.error(msg || fallback);
  };

  const fetchActions = async () => {
    try {
      setActionsLoading(true);
      const { data } = await logActionAPI.getActions();
      if (data?.success && data?.data) setActions(data.data);
    } catch (error) {
      notifyError(error, "Không thể tải danh sách hành động");
    } finally {
      setActionsLoading(false);
    }
  };

  const fetchLogs = async (params = {}) => {
    try {
      setLoading(true);
      // Lưu bộ lọc đang áp dụng (bỏ page) để nút export dùng đúng phạm vi đang xem
      const { page, ...filterOnly } = params;
      setAppliedParams(filterOnly);
      const { data } = await logActionAPI.getLogs(params);
      const result = data?.data;
      if (data?.success && result) {
        setLogData(result.data || []);
        setPagination({
          current: result.current_page || 1,
          pageSize: result.per_page || 30,
          total: result.total || 0,
        });
      } else {
        setLogData([]);
        setPagination({ current: 1, pageSize: 30, total: 0 });
      }
    } catch (error) {
      notifyError(error, "Không thể tải nhật ký");
      setLogData([]);
      setPagination({ current: 1, pageSize: 30, total: 0 });
    } finally {
      setLoading(false);
    }
  };

  const buildParams = (values, page) => {
    const { action, search, is_read, dateRange } = values || {};
    const params = {};
    if (page) params.page = page;
    if (action) params.action = action;
    if (search) params.search = search.trim();
    if (is_read !== undefined && is_read !== "" && is_read !== null) params.is_read = is_read;
    if (dateRange && dateRange.length === 2) {
      params.from_date = dayjs(dateRange[0]).format("YYYY-MM-DD");
      params.to_date = dayjs(dateRange[1]).format("YYYY-MM-DD");
    }
    return params;
  };

  useEffect(() => {
    fetchActions();
    const today = dayjs().format("YYYY-MM-DD");
    form.setFieldsValue({ dateRange: [dayjs(), dayjs()] });
    fetchLogs({ from_date: today, to_date: today });
  }, []);

  const handleSearch = (values) => fetchLogs(buildParams(values));
  const handleRefresh = () => fetchLogs(buildParams(form.getFieldsValue()));
  const handleTableChange = (info) => fetchLogs(buildParams(form.getFieldsValue(), info.current));

  // Đánh dấu đã đọc + cập nhật local (không cần tải lại toàn bảng)
  const markRead = async (ids) => {
    try {
      await logActionAPI.markAsRead(ids);
      setLogData((prev) => prev.map((r) => (ids.includes(r.id) ? { ...r, is_read: true } : r)));
    } catch (error) {
      notifyError(error, "Không thể đánh dấu đã đọc");
    }
  };

  const openDetail = (record) => {
    setDetail(record);
    if (!record.is_read) markRead([record.id]);
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 90,
      render: (id, record) => (
        <Space size={4}>
          <span>{id}</span>
          {!record.is_read && <StatusTag tone="danger">NEW</StatusTag>}
        </Space>
      ),
    },
    {
      title: "Người dùng",
      dataIndex: "user_id",
      key: "user_id",
      width: 190,
      render: (_, record) => (
        <Space>
          <UserOutlined />
          {getDisplayNameFromUser(record?.user, record?.user?.username)}
        </Space>
      ),
    },
    {
      title: "Hành động",
      dataIndex: "action",
      key: "action",
      width: 200,
      render: (action) => <StatusTag>{actions[action] || action}</StatusTag>,
    },
    { title: "Mô tả", dataIndex: "description", key: "description", width: 300, ellipsis: true },
    {
      title: "Địa chỉ IP",
      dataIndex: "ip_address",
      key: "ip_address",
      width: 150,
      render: (ip) => (
        <Space>
          <GlobalOutlined />
          <span>{ip || "N/A"}</span>
        </Space>
      ),
    },
    {
      title: "Thời gian",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (_, record) => formatDateTimeCustomize(record.created_at),
    },
    {
      title: "Thao tác",
      key: "op",
      fixed: "right",
      width: 120,
      align: "center",
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openDetail(record)}>
            Chi tiết
          </Button>
          {!record.is_read && (
            <Button
              type="text"
              size="small"
              icon={<CheckCircleOutlined style={{ color: semantic.success }} />}
              title="Đánh dấu đã đọc"
              onClick={() => markRead([record.id])}
            />
          )}
        </Space>
      ),
    },
  ];

  // Bảng diff cũ -> mới trong modal
  const diffColumns = [
    { title: "Trường thay đổi", dataIndex: "label", key: "label", width: "30%" },
    {
      title: "Giá trị cũ",
      dataIndex: "old",
      key: "old",
      width: "35%",
      render: (val) => (
        <div style={{ background: semantic.dangerSubtle, border: "1px solid #ffa39e", borderRadius: 4, padding: "2px 8px" }}>
          {renderValue(val)}
        </div>
      ),
    },
    {
      title: "Giá trị mới",
      dataIndex: "new",
      key: "new",
      width: "35%",
      render: (val) => (
        <div style={{ background: semantic.successSubtle, border: "1px solid #b7eb8f", borderRadius: 4, padding: "2px 8px" }}>
          {renderValue(val)}
        </div>
      ),
    },
  ];

  const renderDetailBody = () => {
    if (!detail) return null;
    const changed = detail.changed_fields;
    const snapshot = detail.after || detail.before; // create -> after, delete -> before

    let body;
    if (changed && Object.keys(changed).length > 0) {
      const rows = Object.entries(changed).map(([field, v]) => ({
        key: field,
        label: v.label || field,
        old: v.old,
        new: v.new,
      }));
      body = <Table columns={diffColumns} dataSource={rows} pagination={false} size="small" bordered />;
    } else if (snapshot && typeof snapshot === "object") {
      const rows = Object.entries(snapshot).map(([field, val]) => ({ key: field, field, val }));
      body = (
        <Table
          size="small"
          bordered
          pagination={false}
          dataSource={rows}
          columns={[
            { title: "Trường", dataIndex: "field", key: "field", width: "40%" },
            { title: "Giá trị", dataIndex: "val", key: "val", render: (v) => renderValue(v) },
          ]}
        />
      );
    } else {
      body = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Hành động hệ thống — không có dữ liệu thay đổi" />;
    }

    return (
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label="Hành động">
            <StatusTag>{actions[detail.action] || detail.action}</StatusTag>
          </Descriptions.Item>
          <Descriptions.Item label="Thời gian">{formatDateTimeCustomize(detail.created_at)}</Descriptions.Item>
          <Descriptions.Item label="Người dùng">
            {getDisplayNameFromUser(detail?.user, detail?.user?.username)}
          </Descriptions.Item>
          <Descriptions.Item label="Địa chỉ IP">{detail.ip_address || "N/A"}</Descriptions.Item>
          {modelShortName(detail.loggable_type) && (
            <Descriptions.Item label="Đối tượng" span={2}>
              {modelShortName(detail.loggable_type)} #{detail.loggable_id}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Mô tả" span={2}>{detail.description}</Descriptions.Item>
        </Descriptions>
        {body}
      </Space>
    );
  };

  return (
    <BrandedPageLayout
      title="Nhật ký hành động"
      subtitle="Theo dõi hoạt động và thay đổi dữ liệu trên hệ thống (lưu 3 tháng gần nhất)."
      breadcrumbItems={[{ title: "Hệ thống", path: "/action-logs" }, { title: "Nhật ký hành động" }]}
    >
      {/*
        Band 3 — ở màn này bộ lọc là FORM tìm kiếm (khoảng thời gian bắt buộc, bấm
        "Tìm kiếm" mới chạy) nên không dùng ListViewbar dạng bật/tắt từng ô; chỉ
        bọc đúng thẻ toolbar chuẩn để khung 4 dải nhất quán.
      */}
      <Card
        size="small"
        style={{
          marginBottom: spacing[4],
          borderRadius: radius.md,
          border: border.hairline,
          boxShadow: shadow.xs,
        }}
        styles={{ body: { padding: spacing[4] } }}
      >
      <Form form={form} layout="vertical" onFinish={handleSearch} initialValues={{ dateRange: [dayjs(), dayjs()] }}>
        <Row gutter={16} wrap={false} align="bottom">
          <Col style={{ flex: "1 1 0" }}>
            <Form.Item name="action" label={<Space><FilterOutlined /><span>Loại hành động</span></Space>}>
              <Select placeholder="Chọn loại hành động" allowClear loading={actionsLoading} showSearch optionFilterProp="children">
                {Object.entries(actions).map(([key, value]) => (
                  <Option key={key} value={key}>{value}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col style={{ flex: "1 1 0" }}>
            <Form.Item name="search" label={<Space><SearchOutlined /><span>Tìm kiếm</span></Space>}>
              <Input placeholder="Nhập từ khóa..." allowClear />
            </Form.Item>
          </Col>

          <Col style={{ width: 150 }}>
            <Form.Item name="is_read" label="Trạng thái">
              <Select placeholder="Tất cả" allowClear>
                <Option value="0">Chưa xem</Option>
                <Option value="1">Đã xem</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col style={{ flex: "1 1 0" }}>
            <Form.Item
              name="dateRange"
              label={<Space><CalendarOutlined /><span>Khoảng thời gian</span></Space>}
              rules={[{ required: true, message: "Vui lòng chọn khoảng thời gian!" }]}
            >
              <RangePicker style={{ width: "100%" }} format="DD/MM/YYYY" placeholder={["Từ ngày", "Đến ngày"]} />
            </Form.Item>
          </Col>

          <Col>
            <Form.Item label=" ">
              <Space>
                <Button type="primary" htmlType="submit" loading={loading} icon={<SearchOutlined />}>Tìm kiếm</Button>
                <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>Làm mới</Button>
                <ImportExportMenu
                  exportUrl="/api/v1/serviceconnect/logs/export-actions"
                  exportParams={appliedParams}
                  exportFileName="nhat_ky_hanh_dong.xlsx"
                />
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      </Card>

      <CmsTableCard
        title="Kết quả"
        count={pagination.total}
        columns={columns}
        dataSource={logData}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: false,
          showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`,
        }}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có dữ liệu nhật ký" /> }}
      />

      <Modal
        title="Chi tiết nhật ký"
        open={!!detail}
        onCancel={() => setDetail(null)}
        footer={<Button onClick={() => setDetail(null)}>Đóng</Button>}
        width={760}
      >
        {renderDetailBody()}
      </Modal>
    </BrandedPageLayout>
  );
};

export default LogActionScreen;
